/*
 * Copyright (c) 2024 SARL SIGMA POLYNESIA
 */

import { faLink } from '@fortawesome/free-solid-svg-icons/faLink'
import { faPlusCircle } from '@fortawesome/free-solid-svg-icons/faPlusCircle'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { fetchLeaseTemplate, findManyLeaseTemplates } from '../../lib/rest/leaseTemplateAPIClient'
import { useQuery } from '@tanstack/react-query'
import { getHalEmbedded } from '@jalik/hal'
import SearchInput, {
  SearchInputOption,
  SearchInputProps
} from '../../../../client/js/components/form/fields/SearchInput'
import { useIntl } from 'react-intl'
import {
  getLeaseTemplateCreateLink,
  getLeaseTemplateLink
} from '../../lib/links/leaseTemplateLinks'
import AnchorLink from '../../../../client/js/components/AnchorLink'
import { ActionIcon } from '@mantine/core'

export type LeaseTemplateInputProps = Omit<SearchInputProps<LeaseTemplate>, 'data'> & {
  queryFilter?: Record<string, any>,
}

function LeaseTemplateInput (props: LeaseTemplateInputProps) {
  const {
    disabled,
    id,
    label,
    name = 'templateId',
    onChange,
    queryFilter,
    required,
    value,
    ...others
  } = props

  const fieldId = id || `${name}-field`

  const intl = useIntl()
  const [searchParams, setSearchParams] = useState({ limit: 5, search: null })

  const listQuery = useQuery({
    enabled: true,
    queryKey: ['mandates', queryFilter, searchParams],
    queryFn: () => findManyLeaseTemplates({
      ...queryFilter,
      ...searchParams
    }),
    refetchOnWindowFocus: false
  })

  const data = useMemo(() => (
    (listQuery.data != null ? getHalEmbedded(listQuery.data)?.leaseTemplates : null) || []
  ), [listQuery.data])

  const options: SearchInputOption<LeaseTemplate>[] = useMemo(() => (
    data.map((el) => ({
      item: el,
      label: el.name,
      value: String(el.id)
    }))
  ), [data])

  const setSearch = useCallback((search: string) => {
    setSearchParams((s) => ({ ...s, search }))
  }, [])

  const leaseTemplateQuery = useQuery({
    enabled: value != null && value !== '',
    queryKey: ['mandate', value],
    queryFn: () => fetchLeaseTemplate(parseInt(value, 10)),
    refetchOnWindowFocus: false
  })
  const { data: leaseTemplate } = leaseTemplateQuery

  useEffect(() => {
    if (leaseTemplate != null && searchParams.search == null) {
      setSearchParams((s) => ({
        ...s, search: leaseTemplate.name
      }))
    }
  }, [leaseTemplate, searchParams.search])

  return (
    <>
      <SearchInput
        id={fieldId}
        label={label || intl.formatMessage({
          id: 'lease_template',
          defaultMessage: 'Modèle de bail'
        })}
        {...others}
        data={options}
        disabled={disabled}
        name={name}
        onChange={onChange}
        onSearchChange={setSearch}
        required={required}
        searchValue={searchParams.search}
        value={value != null ? String(value) : null}
        rightSection={<>
          {value == null
            ? <ActionIcon
              component={AnchorLink}
              target="_blank"
              title={intl.formatMessage({
                id: 'new',
                defaultMessage: 'Nouveau'
              })}
              to={getLeaseTemplateCreateLink()}
            >
              <FontAwesomeIcon
                fixedWidth
                icon={faPlusCircle}
              />
            </ActionIcon>
            : <ActionIcon
              disabled={!value}
              component={AnchorLink}
              target="_blank"
              to={getLeaseTemplateLink(Number(value))}
            >
              <FontAwesomeIcon
                fixedWidth
                icon={faLink}
              />
            </ActionIcon>
          }
        </>}
      />
    </>
  )
}

export default LeaseTemplateInput
